import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import html2canvas from "html2canvas";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import UseAuth from "shared/common/hooks/UseAuth";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { coordSystems } from "shared/common/utils/constants/coordSystems";
import { addThousandsSeparator } from "shared/common/utils/general-utils";
import PdfReportTemplate from "shared/common/utils/pdf/PdfReportTemplate";
import { FileDownloadOutlined } from "@mui/icons-material";
import { monitor } from "shared/common/services/apm-logging";

interface PdfReportProps {
  remark?: string;
  shareURL?: string;
  buttonDisabled?: boolean;
}

const PdfReport = ({ remark, shareURL, buttonDisabled }: PdfReportProps) => {
  const [generating, setGenerating] = useState<boolean>(false);

  const activeCampaign = defaultStore((state) => state.activeCampaign);
  const projectConfig = defaultStore((state) => state.projectConfig);
  const clientConfig = defaultStore((state) => state.clientConfig);
  const viewer = defaultStore((state) => state.viewer);
  const mapExpand = defaultStore((state) => state.mapExpand);
  const changeMapExpand = defaultStore((state) => state.changeMapExpand);
  const apm = defaultStore((state) => state.apm);

  const { userData } = UseAuth();
  const measurementActiveTool = sidebarStore(
    (state) => state.measurementActiveTool
  );
  const coordinateToolValues = sidebarStore(
    (state) => state.coordinateToolValues
  );
  const distanceToolValues = sidebarStore((state) => state.distanceToolValues);
  const areaToolValues = sidebarStore((state) => state.areaToolValues);
  const verticalDistanceToolValues = sidebarStore(
    (state) => state.verticalDistanceToolValues
  );
  const orthogonalDistanceToolValues = sidebarStore(
    (state) => state.orthogonalDistanceToolValues
  );
  const surveyWheelToolValues = sidebarStore(
    (state) => state.surveyWheelToolValues
  );
  const linrefToolValues = sidebarStore((state) => state.linrefToolValues);

  const { t } = useTranslation();
  const { showMessage } = useSnackbar();

  const handleCapture = async () => {
    if (!viewer) return;
    setGenerating(true);

    // get all image information
    const currentGeoframe = viewer.getScene().getCurrentGeoframe();
    const imageInformation = [
      {
        label: t("image_info.date"),
        value: new Date(currentGeoframe.timestamp).toLocaleString("de"),
      },
      {
        label: `${t("image_info.position")} (${
          coordSystems[currentGeoframe.srid]
            ? coordSystems[currentGeoframe.srid]
            : `EPSG:${currentGeoframe.srid}`
        })`,
        value: `${addThousandsSeparator(currentGeoframe.easting.toFixed(2))}, ${addThousandsSeparator(currentGeoframe.northing.toFixed(2))}`,
      },
      {
        label: t("image_info.height"),
        value: `${addThousandsSeparator(currentGeoframe.height.toFixed(2))} m`,
      },
    ];

    // get all measurements
    let measurements: { label: string; value: string }[] = [];
    if (measurementActiveTool.length > 0) {
      const toolId = measurementActiveTool[0].id;
      if (toolId === "coordinate" && coordinateToolValues) {
        const easting =
          coordinateToolValues.pointfeature.geometry.coordinates[0].toFixed(2);
        const northing =
          coordinateToolValues.pointfeature.geometry.coordinates[1].toFixed(2);
        const height =
          coordinateToolValues.pointfeature.geometry.coordinates[2].toFixed(2);
        measurements = [
          {
            label: t("coordinate.position"),
            value: `${easting}, ${northing}`,
          },
          {
            label: t("coordinate.height"),
            value: `${height} m`,
          },
        ];
      }
      if (toolId === "distance" && distanceToolValues) {
        measurements = [
          {
            label: t("distance.inclined"),
            value: `${distanceToolValues.distance.toFixed(2)} m`,
          },
          {
            label: t("distance.horizontal"),
            value: `${distanceToolValues.distance2d.toFixed(2)} m`,
          },
          {
            label: t("distance.height_difference"),
            value: `${distanceToolValues.height.toFixed(2)} m`,
          },
        ];
      }
      if (toolId === "measureArea" && areaToolValues) {
        measurements = [
          { label: t("area.3d"), value: `${areaToolValues.area.toFixed(2)} m` },
          {
            label: t("area.2d"),
            value: `${areaToolValues.area2d.toFixed(2)} m`,
          },
        ];
      }
      if (toolId === "verticalDistance" && verticalDistanceToolValues) {
        measurements = [
          {
            label: t("vertical.plumb_line"),
            value: `${verticalDistanceToolValues.distance.toFixed(2)} m`,
          },
        ];
      }
      if (toolId === "orthogonalDistance" && orthogonalDistanceToolValues) {
        measurements = [
          {
            label: t("orthogonal.distance"),
            value: `${orthogonalDistanceToolValues.distance.toFixed(2)} m`,
          },
        ];
      }
      if (toolId === "linref") {
        measurements = [
          {
            label: t("linref.axis"),
            value: `${
              viewer.getScene().getLRHandler().getActiveMeasurementAxis()?.name
            }`,
          },
          {
            label: t("measurement.linref_u"),
            value: `${linrefToolValues?.lrcoords[0].toFixed(2)} m`,
          },
          {
            label: t("measurement.linref_v"),
            value: `${linrefToolValues?.lrcoords[1].toFixed(2)} m`,
          },
        ];
        if (linrefToolValues?.lrcoords[2]) {
          measurements.splice(3, 0, {
            label: t("measurement.linref_w"),
            value: `${linrefToolValues.lrcoords[2].toFixed(2)} m`,
          });
        }
        if (clientConfig?.linref?.type !== "rail-ch") {
          if (linrefToolValues?.featureidx === undefined) {
            measurements.splice(1, 0, {
              label: t("linref.ref_point"),
              value: "",
            });
          } else {
            const axis = viewer
              .getScene()
              .getLRHandler()
              .getActiveMeasurementAxis();
            const axisid_obj = axis?.getAxisIdentifier(
              linrefToolValues.featureidx
            );
            const axisid = Object.keys(axisid_obj || {})
              .map((x) => {
                return axisid_obj[x];
              })
              .slice(-1);
            measurements.splice(1, 0, {
              label: t("linref.ref_point"),
              value: axisid.toString() || "",
            });
          }
        }
      }
      if (toolId === "surveyWheel") {
        measurements = [
          {
            label: t("measurement.wheel_distance"),
            value: `${surveyWheelToolValues.toFixed(2)} m`,
          },
        ];
      }
    }

    const originalShowMapWindow = mapExpand;
    changeMapExpand(true);

    // wait for render to update
    setTimeout(
      async () => {
        let dataUrlViewer;
        // get viewer screenshot
        const viewerElement = document.querySelector(
          ".infra3dsdk-viewer"
        ) as HTMLElement;
        if (viewerElement) {
          const canvas = await html2canvas(viewerElement, {
            scale: 1,
          });
          dataUrlViewer = canvas.toDataURL("image/jpeg");
        }

        let dataUrlMap;
        // get map screenshot
        const mapElement = document.querySelector(".infra3dmap") as HTMLElement;
        if (mapElement) {
          const canvas = await html2canvas(mapElement, { scale: 1 });
          dataUrlMap = canvas.toDataURL("image/jpeg");
        }

        changeMapExpand(originalShowMapWindow);

        const blob = await pdf(
          <PdfReportTemplate
            viewerImage={dataUrlViewer}
            mapImage={dataUrlMap}
            createdBy={userData?.usermail}
            measurements={measurements}
            imageInformation={imageInformation}
            campaignName={activeCampaign?.description}
            projectName={projectConfig?.name}
            remark={remark?.replace(/\n/g, " ")}
            shareURL={shareURL}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${activeCampaign?.description} - ${projectConfig?.name} - Report.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up

        showMessage(t("share_view.pdf_report_success"), "success");
        setGenerating(false);
      },
      originalShowMapWindow ? 0 : 1500
    );
  };

  return (
    <Box id="pdfreport">
      <LoadingButton
        disabled={buttonDisabled}
        onClick={() => monitor(apm, "export-pdf | Toolbar", handleCapture)}
        loading={generating}
        variant="contained"
        endIcon={<FileDownloadOutlined fontSize="small" />}
      >
        {t("share_view.export")}
      </LoadingButton>
    </Box>
  );
};

export default PdfReport;
